import { TZoneTypes, getUserPref } from "@eventr/common";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";

/**
 * In-memory mirror of the handful of user preferences the UI needs to react to.
 *
 * The durable copy of these values lives in `localStorage["userPreferences"]` and is written by
 * `setUserPref(...)` at each call site (logout clears it). This store is only *seeded* from
 * `getUserPref(...)` at module evaluation — it is deliberately NOT persisted, and it is
 * deliberately NOT registered with `resetAllStores()`, so that its lifecycle matches the signals
 * it replaced exactly.
 */
export interface UserPrefsState {
  /** Which timezone itinerary dates/times are rendered in. */
  showDestTime: TZoneTypes;
  /** Which sign-in form the auth screen defaults to. */
  loginMethod: "password" | "email";
  /** Whether the dev overlay is mounted. */
  showDevOverlay: "dev" | "prod";
  setShowDestTime: (value: TZoneTypes) => void;
  setLoginMethod: (value: "password" | "email") => void;
  setShowDevOverlay: (value: "dev" | "prod") => void;
}

const sortShowDevOverlayFromPref = (): "dev" | "prod" => {
  const userPref = getUserPref("account", "showDevOverlay");
  if (["dev", "prod"].includes(userPref)) {
    return userPref;
  }
  return ["dev", "test", "local"].includes(localStorage.getItem("REACT_APP_STAGE") ?? "") ? "dev" : "prod";
};

export const useUserPrefsStore = create<UserPrefsState>()(
  subscribeWithSelector(
    devtools(
      (set) => ({
        showDestTime: (getUserPref("itinerary", "showDestTime") ?? "itinLocal") as TZoneTypes,
        loginMethod: (getUserPref("account", "loginMethod") ?? "email") as "password" | "email",
        showDevOverlay: sortShowDevOverlayFromPref(),
        setShowDestTime: (value) => set({ showDestTime: value }, false, "setShowDestTime"),
        setLoginMethod: (value) => set({ loginMethod: value }, false, "setLoginMethod"),
        setShowDevOverlay: (value) => set({ showDevOverlay: value }, false, "setShowDevOverlay"),
      }),
      { enabled: DEVTOOLS_ENABLED, name: "userPrefs" },
    ),
  ),
);
